import * as R from 'ramda';
import React, { useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import {
  calculateRawDatasetSummary,
  getVisibleSelectedParameters,
  getFilteredDataset,
  setSelectedStats
} from 'store';

import { setDataset, getDataset, getDatasetName } from 'store/dataset';
import { resetParametersInferentialStatisticsPage } from 'store/scenes/inferentialStatistics/actions';

import {
  addRowIndexAndHandleEmptyCell,
  formatColumnHeadersForMaterialTable
} from '../helper';

import withFullScreen from 'components/FullScreenable';

import { getMaterialTableIcons as getTableIcons } from './MaterialTableIcons';
import PagedPreviewTable from '../PagedPreviewTable';
import { Loader } from '../../Loader/Loader';

const numberColumnStyles = {
  background: '#f7f7f7',
  borderRight: '2px solid #dfdfdf'
};

const RowIndexAsNumberColumn = {
  title: 'Row #',
  field: 'rowIndex',
  editable: 'never',
  cellStyle: numberColumnStyles,
  headerStyle: {
    paddingRight: 0
  }
};


const PreviewTableComponent = () => {
  const dataset = useSelector(state => getDataset(state));
  const nameOfDataSet = useSelector(state => getDatasetName(state));
  const filteredDataset = useSelector(state => getFilteredDataset(true)(state));
  const parameters = useSelector(state => getVisibleSelectedParameters(state));

  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [isEditableAddRow, setIsEditableAddRow] = useState(false);


  const parseFromTheEmptyValue = value => (value || +value === 0 ? value : 'empty');
  const parseFromTheEmptyRow = R.map(parseFromTheEmptyValue);

  const handleEmptyCellsInRow = newRow => {
    const rowKeys = R.keys(parameters);
    const newRowKeys = R.keys(newRow);

    if (rowKeys.length === newRowKeys.length) {
      return newRow;
    }

    let emptyRow = {};
    rowKeys.forEach(key => {
      const {
        [key]: { targetType }
      } = parameters;
      const isNumerical = targetType === 'numerical';

      emptyRow = R.mergeRight(emptyRow, { [key]: isNumerical ? Number.NaN : '' });
    });

    return R.mergeRight(emptyRow, newRow);
  };

  const handleRowAdd = newRow =>
    new Promise(resolve => {
      setIsLoading(true);
      setTimeout(() => {
        const clonedDataset = R.clone(dataset);
        const newDatasetRow = R.omit(['tableData', 'rowIndex', 'additionalData'], handleEmptyCellsInRow(newRow));

        clonedDataset.push(parseFromTheEmptyRow(newDatasetRow));

        dispatch(setDataset(clonedDataset));
        dispatch(resetParametersInferentialStatisticsPage());
        dispatch(calculateRawDatasetSummary());
        dispatch(setSelectedStats());

        setIsEditableAddRow(false);

        return resolve();
      });
    }).then(() => setIsLoading(false));

  const handleRowUpdate = updatedRow =>
    new Promise(resolve => {
      setIsLoading(true);
      setTimeout(() => {
        const clonedDataset = R.clone(dataset);
        const {
          additionalData: { rowIndex: updatedRowIndex }
        } = updatedRow;
        const originalDatasetRow = dataset[updatedRowIndex];
        const updatedDatasetRow = R.omit(['tableData', 'rowIndex', 'additionalData'], updatedRow);

        const keysArr = Object.keys(updatedDatasetRow);
        const valuesArr = Object.values(updatedDatasetRow);
        const updatedRowWithCorrectTypes = Object.fromEntries(keysArr.map((_, i) => [keysArr[i], isNaN(+valuesArr[i]) ? valuesArr[i] : +valuesArr[i]]));

        clonedDataset.splice(
          updatedRowIndex,
          1,
          parseFromTheEmptyRow({ ...originalDatasetRow, ...updatedRowWithCorrectTypes })
        );

        dispatch(setDataset(clonedDataset));
        dispatch(resetParametersInferentialStatisticsPage());
        dispatch(calculateRawDatasetSummary());
        dispatch(setSelectedStats());


        return resolve();
      });
    }
    ).then(() => setIsLoading(false));

  const handleRowDelete = deletedRow =>
    new Promise(resolve => {
      setIsLoading(true);
      setTimeout(() => {
        const {
          additionalData: { rowIndex: deletedRowIndex }
        } = deletedRow;
        const clonedDataset = R.clone(dataset);

        clonedDataset.splice(deletedRowIndex, 1);

        dispatch(setDataset(clonedDataset));
        dispatch(resetParametersInferentialStatisticsPage());
        dispatch(calculateRawDatasetSummary());
        dispatch(setSelectedStats());

        return resolve();
      });
    }
    ).then(() => setIsLoading(false));

  const optionsForEditing = {
    onRowAdd: handleRowAdd,
    onRowUpdate: handleRowUpdate,
    onRowDelete: handleRowDelete,
    onRowAddCancelled: () => setIsEditableAddRow(false)
  };

  const columns = formatColumnHeadersForMaterialTable(filteredDataset, parameters, ['additionalData']);
  const formattedData = addRowIndexAndHandleEmptyCell(filteredDataset);
  const icons = getTableIcons(isEditableAddRow, setIsEditableAddRow);

  return (
    <div data-testid="preview-table">
      <h2 className="sub-header-page">
        {nameOfDataSet}
      </h2>
      <span className="mb-15 block">
        <b>Tip:</b> Start your analysis with the <b>Descriptive Statistics</b> tab.
      </span>
      <div className="containerUsualTable" id="previewTable">
        {isLoading ? <Loader />
          : <PagedPreviewTable
            icons={icons}
            columns={[RowIndexAsNumberColumn, ...columns]}
            data={formattedData}
            editableOptions={optionsForEditing}
          />
        }
      </div>
    </div>
  );
};

export default withFullScreen(PreviewTableComponent);
